import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { ACCENT_GOLD, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { CoinIcon, IconSkillTree, IconCheck } from '../ui/icons';
import { claimOriginFromEvent } from './claimOrigin';
import ClaimableAura from './ClaimableAura';
import { k, t } from '../../localization/t';

// Borderless reward cell. No frame, no background. Just the icon
// plus the amount + label stack, with a small state pill underneath.
// Highlight (gold glow + brighten) appears on hover for claimable
// cells only. Disabled / received / locked states use solid muted
// colors per the no-transparency rule, never `opacity:`.

// Solid colors only. Earlier passes used `background-clip: text` for
// the amount, but Electron rendered short gradient-clipped strings
// like "+50" as solid colored rectangles instead of glyphs. Almost
// certainly the drop-shadow filter interacting badly with the text-
// mask path. Solid hex colors render reliably and also match the
// no-transparency rule cleanly.
const PALETTE = {
  claimable: { icon: ACCENT_GOLD, amount: '#fbe9b8', label: TEXT_PRIMARY, state: ACCENT_GOLD },
  claiming:  { icon: ACCENT_GOLD, amount: '#fbe9b8', label: TEXT_PRIMARY, state: ACCENT_GOLD },
  received:  { icon: '#8a7548',   amount: '#c6b486', label: '#7d6e4c',    state: '#7d6e4c' },
  locked:    { icon: '#5b5247',   amount: '#6b6052', label: '#5b5247',    state: '#5b5247' },
};

const TYPE_META = {
  gold:  { Icon: CoinIcon,      label: k('Gold') },
  skill: { Icon: IconSkillTree, label: k('Skill Point') },
};

export default class RewardCell extends Component {
  state = { hovered: false };

  handleClick = (e) => {
    e.stopPropagation();
    if (this.props.state !== 'claimable' || !this.props.onClaim) return;
    this.props.onClaim(claimOriginFromEvent(e));
  };

  render() {
    const { type, amount, state, lockedLevel } = this.props;
    const meta = TYPE_META[type];
    if (!meta) return null;
    const { Icon } = meta;
    const label = t(meta.label);
    const palette = PALETTE[state] || PALETTE.locked;
    const isClaimable = state === 'claimable';
    const isClaiming = state === 'claiming';
    const isReceived = state === 'received';
    const { hovered } = this.state;

    return (
      <motion.button
        type="button"
        disabled={!isClaimable}
        onClick={this.handleClick}
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '0.4cqw 0.7cqw',
          // Fixed cell width. Prevents the row from shifting when
          // the state text swaps from "Claim" (5 chars) to "Received"
          // (8 chars + check icon) on click. The bottom state line
          // also has a fixed width below for the same reason.
          width: '9cqw',
          height: '5.6cqw',
          display: 'flex',
          alignItems: 'center',
          gap: '0.7cqw',
          position: 'relative',
          transform: hovered && isClaimable ? 'translateY(-0.2cqw)' : 'translateY(0)',
          transition: 'transform 180ms cubic-bezier(0.2,0.9,0.25,1.05)',
        }}
        aria-label={isClaimable ? t('Claim {amount} {label}', { amount, label }) : t('{label} {state}', { label, state })}
 >
 {/* Resting "claim me" aura. Two layered radial gradients
 breathing on offset cycles, additively brightening the
 canvas beneath. Always visible on claimable cells so the
 player can spot what's still claimable from across the
 screen at a glance. */}
 {isClaimable ? <ClaimableAura /> : null}

 {/* Hover glow. Only on claimable, only while hovered.
 Sits behind the content as a soft radial halo so the
 content stays crisp on top. Solid radial-gradient
 (no rgba background), shadow uses alpha (allowed for
 shadows per the no-transparency rule). */}
 <AnimatePresence>
 {hovered && isClaimable ? (
 <motion.div
 key="halo"
 aria-hidden
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.18 }}
 style={{
 position: 'absolute',
 inset: '-0.4cqw',
 borderRadius: 'var(--radius-lg)',
 boxShadow: 'inset 0 0 22px rgba(212, 168, 67, 0.32), 0 0 18px rgba(212, 168, 67, 0.35)',
 pointerEvents: 'none',
 }}
 />
 ) : null}
 </AnimatePresence>

 <motion.div
 animate={isClaiming ? { scale: 1.3, rotate: 10 } : { scale: 1 }}
 transition={{ type: 'spring', stiffness: 260, damping: 14 }}
 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
 >
 <Icon size="2.4cqw" color={palette.icon} glow={isClaimable} />
 </motion.div>

 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.1cqw', minWidth: 0 }}>
 <span
 className="arena-heading font-bold tabular-nums"
 style={{
 fontSize: '1.4cqw',
 lineHeight: 1,
 color: palette.amount,
 textShadow: '0 1px 2px rgba(0,0,0,0.75)',
 }}
 >
 +{amount}
 </span>
 <span
 style={{
 fontSize: '0.55cqw',
 color: palette.label,
 fontWeight: 700,
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 whiteSpace: 'nowrap',
 }}
 >
 {label}
 </span>
 <span
 style={{
 fontSize: '0.5cqw',
 color: palette.state,
 fontWeight: 700,
 marginTop: '0.1cqw',
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 whiteSpace: 'nowrap',
 display: 'flex',
 alignItems: 'center',
 gap: '0.3cqw',
 // Reserved width so the cell never reflows when state
 // text swaps. The widest variant is "Received" with
 // the check icon, sized accordingly.
 width: '3.6cqw',
 height: '0.65cqw',
 }}
 >
 {isReceived ? (
 <>
 <IconCheck size="0.65cqw" color={palette.state} />
 {t('Received')}
 </>
 ) : isClaiming ? (
 '...'
 ) : isClaimable ? (
 t('Claim')
 ) : (
 t('Lvl {n}', { n: lockedLevel })
            )}
          </span>
        </div>

        {isClaiming ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: '50%',
              right: '0.3cqw',
              transform: 'translateY(-50%)',
              width: '0.9cqw',
              height: '0.9cqw',
              borderRadius: '50%',
              border: '1.5px solid #3a2f1c',
              borderTopColor: ACCENT_GOLD,
              animation: 'progSpin 0.9s linear infinite',
            }}
          />
        ) : null}
      </motion.button>
    );
  }
}
